import React, { useState } from 'react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import { useT } from '../i18n';
import { BACKEND_URL } from '../runtimeConfig';
import { AFTER_INSTALL, SERVER_SPEC, installCommand, keyPlaceholder } from '../lib/serverSpec';
import {
  SELF_HOSTED_DOMAIN_HINT,
  SELF_HOSTED_EMAIL_HINT,
  SELF_HOSTED_INSTALL,
  SELF_HOSTED_KEY_HINT,
} from '../i18n/selfHosted';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Copy, Terminal } from 'lucide-react';

const ROLES = ['production', 'test'];

/*
  Enterprise: what server to get and the one command that installs the
  tracker on it. The key is filled in right after it is issued (it is shown
  only once); otherwise the customer pastes it over the X placeholder.
*/
export default function SelfHostedInstall({ licenceKey, keyHint }) {
  const { t } = useT();
  const { user } = useAuth();
  const [role, setRole] = useState('production');

  const command = installCommand({
    base: BACKEND_URL || window.location.origin,
    key: licenceKey || keyPlaceholder(keyHint),
    email: (user && user.email) || 'admin@example.com',
    role,
  });

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(command);
      toast.success(t('Copied'));
    } catch (_e) {
      toast.error(t('Action failed'));
    }
  };

  return (
    <Card className="mb-6" data-testid="self-hosted-install">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
          <Terminal className="w-4 h-4" /> {t('Install on your server')}
          <Button asChild size="sm" variant="outline" className="ml-auto">
            <Link to="/self-hosted/guide" data-testid="install-guide-link">
              {t('Step-by-step guide')}
            </Link>
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6 text-sm">
        <div>
          <h4 className="mb-2 font-medium">{t('Server requirements')}</h4>
          <dl className="grid grid-cols-1 gap-x-4 gap-y-1.5 sm:grid-cols-[180px_1fr]" data-testid="server-spec">
            {SERVER_SPEC.map((row) => (
              <React.Fragment key={row.label}>
                <dt className="text-muted-foreground">{t(row.label)}</dt>
                <dd>{t(row.value)}</dd>
              </React.Fragment>
            ))}
          </dl>
        </div>

        <div className="space-y-3">
          <p className="text-muted-foreground">{t(SELF_HOSTED_INSTALL)}</p>
          <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={t('Install on your server')}>
            {ROLES.map((key) => (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={role === key}
                onClick={() => setRole(key)}
                className={
                  'rounded-lg border px-3 py-1.5 text-xs transition-colors ' +
                  (role === key
                    ? 'border-primary bg-primary/10 font-medium text-primary'
                    : 'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground')
                }
                data-testid={'install-role-' + key}
              >
                {key === 'test' ? t('Test server') : t('Production server')}
              </button>
            ))}
          </div>
          <div className="rounded-lg border border-border bg-muted/40">
            <pre className="overflow-x-auto p-4 font-mono text-xs leading-relaxed" data-testid="install-command">
              {command}
            </pre>
            <div className="flex justify-end border-t border-border px-3 py-2">
              <Button size="sm" variant="outline" onClick={copy} data-testid="install-copy">
                <Copy className="w-3.5 h-3.5 mr-1.5" /> {t('Copy command')}
              </Button>
            </div>
          </div>
          <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
            {!licenceKey && <li>{t(SELF_HOSTED_KEY_HINT)}</li>}
            <li>{t(SELF_HOSTED_EMAIL_HINT)}</li>
            <li>{t(SELF_HOSTED_DOMAIN_HINT)}</li>
          </ul>
        </div>

        <div>
          <h4 className="mb-2 font-medium">{t('After the install')}</h4>
          <dl className="grid grid-cols-1 gap-x-4 gap-y-1.5 sm:grid-cols-[180px_1fr]">
            {AFTER_INSTALL.map(([cmd, label]) => (
              <React.Fragment key={cmd}>
                <dt className="font-mono text-xs leading-5">{cmd}</dt>
                <dd className="text-muted-foreground">{t(label)}</dd>
              </React.Fragment>
            ))}
          </dl>
        </div>
      </CardContent>
    </Card>
  );
}
